import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { employeeAuthApi } from './api/authApi';
import { appToast } from '../../../components/AppToast';
import { parseStoredUser } from '../../../utils/authSession';

export default function EmployeeLoginScreen() {
  const navigate = useNavigate();
  const [tenantUsername, setTenantUsername] = useState(
    () => localStorage.getItem('tenantUsername') || ''
  );
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const user = parseStoredUser();
    if (token && (user?.role === 'employee' || user?.is_employee)) {
      navigate('/self-service/dashboard', { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!tenantUsername.trim()) {
      setErrorMsg('Please enter your Tenant Username (Organisation Code).');
      return;
    }
    if (!username.trim() || !password) {
      setErrorMsg('Please enter your Employee Code / Email / Mobile and Password.');
      return;
    }
    setErrorMsg('');
    setIsSubmitting(true);

    try {
      const data = await employeeAuthApi.login(
        tenantUsername.trim(),
        username.trim(),
        password
      );
      if (data?.token) {
        localStorage.setItem('tenantUsername', tenantUsername.trim().toUpperCase());
        localStorage.setItem('authToken', data.token);
        localStorage.setItem(
          'user',
          JSON.stringify({
            id: data.employeeId,
            acHeadId: data.employeeId,
            name: data.employeeName,
            email: data.email,
            mobile: data.mobile,
            designation: data.designation,
            role: 'employee',
            is_employee: true,
            modules: ['self-service'],
            tenantId: data.tenantId,
            tenantUsername: data.tenantUsername || tenantUsername.trim().toUpperCase(),
          })
        );
        appToast.success(`Welcome back, ${data.employeeName}!`);
        navigate('/self-service/dashboard');
      }
    } catch (err) {
      const detail =
        err.response?.data?.detail ||
        'Invalid employee credentials or tenant username. Please verify and try again.';
      setErrorMsg(detail);
      appToast.error(detail);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-container-lowest px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-7 rounded-3xl border border-outline-variant/30 bg-surface p-8 shadow-xl sm:p-10">
        <div className="text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary shadow-inner">
            <span className="material-symbols-outlined text-3xl">badge</span>
          </div>
          <h2 className="mt-4 text-2xl font-extrabold tracking-tight text-on-surface">
            Employee Self-Service
          </h2>
          <p className="mt-1.5 text-xs font-medium text-on-surface-variant">
            Sign in with your organization tenant username and employee credentials
          </p>
        </div>

        {errorMsg && (
          <div className="rounded-xl border border-rose-300 bg-rose-50 p-3 text-xs font-semibold text-rose-700 animate-in fade-in duration-200">
            {errorMsg}
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          {/* Tenant Username */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-on-surface-variant">
              Tenant Username (Organisation Code)
            </label>
            <div className="relative mt-1">
              <span className="material-symbols-outlined pointer-events-none absolute left-3 top-2.5 text-slate-400 text-lg">
                domain
              </span>
              <input
                type="text"
                required
                value={tenantUsername}
                onChange={(e) => setTenantUsername(e.target.value.toUpperCase())}
                placeholder="e.g. BOSS0001"
                className="w-full uppercase tracking-wider rounded-xl border border-outline-variant/40 bg-surface-container-lowest pl-10 pr-3 py-2 text-xs font-bold text-on-surface focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary placeholder:normal-case placeholder:font-normal"
              />
            </div>
          </div>

          {/* Employee Identifier */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-on-surface-variant">
              Employee Code, Email, or Mobile
            </label>
            <div className="relative mt-1">
              <span className="material-symbols-outlined pointer-events-none absolute left-3 top-2.5 text-slate-400 text-lg">
                person
              </span>
              <input
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="EMP001, name@company.com or 98200..."
                className="w-full rounded-xl border border-outline-variant/40 bg-surface-container-lowest pl-10 pr-3 py-2 text-xs font-medium text-on-surface focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-on-surface-variant">
              Portal Password
            </label>
            <div className="relative mt-1">
              <span className="material-symbols-outlined pointer-events-none absolute left-3 top-2.5 text-slate-400 text-lg">
                lock
              </span>
              <input
                type={showPassword ? 'text' : 'password'}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full rounded-xl border border-outline-variant/40 bg-surface-container-lowest pl-10 pr-10 py-2 text-xs font-medium text-on-surface focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 top-2.5 text-slate-400 hover:text-on-surface transition cursor-pointer"
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                <span className="material-symbols-outlined text-lg">
                  {showPassword ? 'visibility_off' : 'visibility'}
                </span>
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-primary py-2.5 text-xs font-bold text-on-primary shadow-sm hover:bg-primary/90 transition cursor-pointer disabled:opacity-50 mt-2"
          >
            {isSubmitting ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                <span>Signing In...</span>
              </>
            ) : (
              <>
                <span>Sign In to Portal</span>
                <span className="material-symbols-outlined text-base">login</span>
              </>
            )}
          </button>
        </form>

        <div className="mt-6 flex flex-col gap-3 border-t border-outline-variant/20 pt-4 text-xs text-center text-on-surface-variant">
          <p className="text-[11px] leading-relaxed">
            Employees are provisioned by Administrator in the Employee Master. If you need credentials or password reset, please contact HR.
          </p>
          <div className="pt-1">
            <Link
              to="/login"
              className="font-bold text-primary hover:underline inline-flex items-center gap-1"
            >
              <span className="material-symbols-outlined text-sm">shield_person</span>
              <span>Switch to Admin / Staff Sign In</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
